import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CardHeader,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
  CircularProgress,
  Alert,
  Button,
  Avatar,
  Paper
} from '@mui/material';
import {
  School as TrainingIcon,
  Assignment as AssignmentIcon,
  CheckCircle as CompletedIcon,
  Warning as OverdueIcon,
  Schedule as PendingIcon,
  TrendingUp as TrendingIcon,
  Person as PersonIcon,
  Business as BusinessIcon,
  LocationOn as LocationIcon,
  Checklist as ChecklistIcon,
  Description as DocumentIcon
} from '@mui/icons-material';
import api from '../utils/api';
import { calendarDaysUntil, formatDateOnly } from '../utils/dateFormat';
import { useAuth } from '../contexts/AuthContext';

interface Assignment {
  id: number;
  content_title: string;
  status: string;
  is_overdue: boolean;
  is_completed?: boolean;
  due_date: string;
  assignment_type: 'training' | 'checklist' | 'document' | 'log';
  assigned_by: {
    first_name: string;
    last_name: string;
  };
  created_at: string;
  document_acknowledgment?: {
    status: string;
  } | null;
  assignment_completions?: Array<{
    status: string;
    assignee?: {
      id: number;
    };
  }>;
}

const Dashboard: React.FC = () => {
  const { user } = useAuth();
  const [assignments, setAssignments] = useState<Assignment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [metrics, setMetrics] = useState<{
    overdue_assignments: number;
    due_soon_assignments: number;
    users_in_compliance: number;
    users_out_of_compliance: number;
    total_active_users: number;
  } | null>(null);

  useEffect(() => {
    fetchMyAssignments();
    fetchMetrics();
  }, []);

  const fetchMyAssignments = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get('/assignments/my_assignments/');
      const list = Array.isArray(response.data) ? response.data : (response.data?.results || []);
      setAssignments(list);
    } catch (error) {
      console.error('Error fetching assignments:', error);
      setError('Failed to load assignment data');
    } finally {
      setLoading(false);
    }
  };

  const fetchMetrics = async () => {
    try {
      const response = await api.get('/assignments/metrics/');
      setMetrics(response.data);
    } catch (err) {
      console.error('Error fetching metrics:', err);
    }
  };

  const getStatusColor = (status: string) => {
    const colors: { [key: string]: string } = {
      'pending': 'warning',
      'in_progress': 'info',
      'completed': 'success',
      'overdue': 'error',
      'cancelled': 'default'
    };
    return colors[status] || 'default';
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return 'No due date';
    return formatDateOnly(dateString, 'No due date');
  };

  const getDaysUntilDue = (dueDate: string) => calendarDaysUntil(dueDate);

  const isCompliant = (assignment: Assignment) => {
    if (assignment.status === 'completed' || assignment.is_completed) {
      return true;
    }
    if (assignment.document_acknowledgment?.status === 'acknowledged') {
      return true;
    }
    return (assignment.assignment_completions || []).some(
      (completion) => completion.status === 'completed' && completion.assignee?.id === user?.id
    );
  };

  const getAssignmentIcon = (assignmentType: string) => {
    switch (assignmentType) {
      case 'training':
        return <TrainingIcon color="primary" />;
      case 'checklist':
        return <ChecklistIcon color="primary" />;
      case 'document':
        return <DocumentIcon color="primary" />;
      default:
        return <TrainingIcon color="primary" />;
    }
  };

  const pendingAssignments = assignments.filter(a => a.status === 'pending' && !a.is_overdue);
  const overdueAssignments = assignments.filter(a => a.is_overdue);
  const inProgressAssignments = assignments.filter(a => a.status === 'in_progress');
  const completedAssignments = assignments.filter(a => a.status === 'completed');

  const recentAssignments = assignments
    .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
    .slice(0, 5);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <Typography variant="h4" component="h1" gutterBottom>
          Dashboard
        </Typography>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button variant="contained" onClick={fetchMyAssignments}>
          Retry
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" component="h1" gutterBottom sx={{ fontSize: { xs: '1.75rem', md: '2.125rem' } }}>
        Dashboard
      </Typography>

      {/* Welcome Section */}
      {user && (
        <Paper sx={{ p: { xs: 2, md: 3 }, mb: 4, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'flex-start', sm: 'center' }, gap: { xs: 2, sm: 3 } }}>
            <Avatar sx={{ width: { xs: 60, md: 80 }, height: { xs: 60, md: 80 }, bgcolor: 'rgba(255, 255, 255, 0.2)' }}>
              <PersonIcon sx={{ fontSize: { xs: 30, md: 40 } }} />
            </Avatar>
            <Box sx={{ color: 'white', flex: 1, width: { xs: '100%', sm: 'auto' } }}>
              <Typography variant="h5" gutterBottom sx={{ fontSize: { xs: '1.25rem', md: '1.5rem' } }}>
                Welcome back, {user.first_name}!
              </Typography>
              <Typography variant="body1" sx={{ opacity: 0.9, mb: 1, fontSize: { xs: '0.875rem', md: '1rem' } }}>
                {user.role.charAt(0).toUpperCase() + user.role.slice(1)} • {user.email}
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: { xs: 1, sm: 3 }, mt: 2 }}>
                {user.department && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <BusinessIcon sx={{ fontSize: 16 }} />
                    <Typography variant="body2" sx={{ opacity: 0.8 }}>
                      {user.department.name}
                    </Typography>
                  </Box>
                )}
                {user.location && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <LocationIcon sx={{ fontSize: 16 }} />
                    <Typography variant="body2" sx={{ opacity: 0.8 }}>
                      {user.location.name}
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>
            <Box sx={{ textAlign: { xs: 'left', sm: 'right' }, color: 'white', width: { xs: '100%', sm: 'auto' } }}>
              <Typography variant="body2" sx={{ opacity: 0.8 }}>
                Last login
              </Typography>
              <Typography variant="body2">
                {user.last_login_at 
                  ? new Date(user.last_login_at).toLocaleDateString()
                  : 'First time login'
                }
              </Typography>
            </Box>
          </Box>
        </Paper>
      )}
      
      {/* Statistics Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="h3" color="warning.main">
                    {pendingAssignments.length}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Pending Assignments
                  </Typography>
                </Box>
                <PendingIcon color="warning" sx={{ fontSize: 40 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="h3" color="error">
                    {metrics?.overdue_assignments ?? overdueAssignments.length}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Overdue Assignments
                  </Typography>
                </Box>
                <OverdueIcon color="error" sx={{ fontSize: 40 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="h3" color="info.main">
                    {metrics?.due_soon_assignments ?? inProgressAssignments.length}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Due In Next 7 Days
                  </Typography>
                </Box>
                <AssignmentIcon color="info" sx={{ fontSize: 40 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        {user && (user.role === 'admin' || user.role === 'manager') && (
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="h3" color="success.main">
                      {metrics ? metrics.users_in_compliance : completedAssignments.length}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Users In Compliance
                    </Typography>
                  </Box>
                  <CompletedIcon color="success" sx={{ fontSize: 40 }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>

      {/* Compliance Split and Quick Actions for Manager/Admin */}
      {metrics && user && (user.role === 'admin' || user.role === 'manager') && (
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} md={6}>
            <Card sx={{ height: '100%' }}>
              <CardHeader title="Compliance Snapshot" />
              <CardContent sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', minHeight: '120px' }}>
                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: { xs: 'flex-start', sm: 'space-between' }, gap: { xs: 2, sm: 0 } }}>
                  <Box>
                    <Typography variant="h4" color="success.main" sx={{ fontSize: { xs: '1.75rem', md: '2.125rem' } }}>
                      {metrics.users_in_compliance}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      In Compliance
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="h4" color="error.main" sx={{ fontSize: { xs: '1.75rem', md: '2.125rem' } }}>
                      {metrics.users_out_of_compliance}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Out of Compliance
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="h4" sx={{ fontSize: { xs: '1.75rem', md: '2.125rem' } }}>
                      {metrics.total_active_users}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Active Users
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          
          {/* Quick Actions for Manager/Admin */}
          <Grid item xs={12} md={6}>
            <Card sx={{ height: '100%' }}>
              <CardHeader title="Quick Actions" />
              <CardContent sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', minHeight: '120px' }}>
                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 1 }}>
                  <Button 
                    variant="contained" 
                    color="primary" 
                    size="small"
                    onClick={() => window.location.href = '/my-assignments'}
                    sx={{ flex: { xs: 'none', sm: 1 }, minWidth: { xs: '100%', sm: '120px' } }}
                  >
                    My Assignments
                  </Button>
                  <Button 
                    variant="outlined" 
                    color="secondary" 
                    size="small"
                    onClick={() => window.location.href = '/assignments'}
                    sx={{ flex: { xs: 'none', sm: 1 }, minWidth: { xs: '100%', sm: '120px' } }}
                  >
                    Manage Assignments
                  </Button>
                  <Button 
                    variant="outlined" 
                    color="info" 
                    size="small"
                    onClick={() => window.location.href = '/notifications'}
                    sx={{ flex: { xs: 'none', sm: 1 }, minWidth: { xs: '100%', sm: '120px' } }}
                  >
                    Notifications
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Recent Activity and Quick Actions */}
      <Grid container spacing={3}>
        {/* Recent Assignments */}
        <Grid item xs={12} md={user && (user.role === 'admin' || user.role === 'manager') ? 12 : 8}>
          <Card>
            <CardHeader 
              title="Recent Assignments" 
              action={
                <Chip 
                  icon={<TrendingIcon />} 
                  label={`${(assignments || []).length} Total`} 
                  color="primary" 
                  size="small" 
                />
              }
            />
            <CardContent>
              {recentAssignments.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No assignments yet.
                </Typography>
              ) : (
                <List>
                  {recentAssignments.map((assignment) => {
                    const daysUntilDue = getDaysUntilDue(assignment.due_date);
                    
                    return (
                      <ListItem 
                        key={assignment.id} 
                        divider
                        sx={{ 
                          flexDirection: { xs: 'column', sm: 'row' },
                          alignItems: { xs: 'flex-start', sm: 'center' },
                          gap: { xs: 1, sm: 0 },
                          py: { xs: 2, sm: 1 }
                        }}
                      >
                        <ListItemIcon sx={{ minWidth: { xs: 40, sm: 56 }, alignSelf: { xs: 'flex-start', sm: 'center' } }}>
                          {getAssignmentIcon(assignment.assignment_type)}
                        </ListItemIcon>
                        <ListItemText
                          primary={assignment.content_title}
                          secondary={
                            <Box>
                              <Typography variant="body2" color="text.secondary" sx={{ wordBreak: 'break-word' }}>
                                Assigned by: {assignment.assigned_by?.first_name} {assignment.assigned_by?.last_name}
                              </Typography>
                              <Typography variant="body2" color="text.secondary" sx={{ wordBreak: 'break-word' }}>
                                Due: {formatDate(assignment.due_date)}
                                {daysUntilDue !== null && !isCompliant(assignment) && (
                                  <span style={{ 
                                    color: daysUntilDue < 0 ? '#f44336' : daysUntilDue <= 3 ? '#ff9800' : '#4caf50',
                                    fontWeight: 'bold'
                                  }}>
                                    {daysUntilDue < 0 
                                      ? ` (${Math.abs(daysUntilDue)} days overdue)`
                                      : daysUntilDue === 0 
                                        ? ' (Due today)'
                                        : ` (${daysUntilDue} days left)`
                                    }
                                  </span>
                                )}
                              </Typography>
                            </Box>
                          }
                          sx={{ flex: 1, minWidth: 0 }}
                        />
                        <Chip 
                          label={assignment.status.replace('_', ' ').toUpperCase()}
                          color={getStatusColor(assignment.status) as any}
                          size="small"
                          sx={{ alignSelf: { xs: 'flex-start', sm: 'center' }, mt: { xs: 1, sm: 0 } }}
                        />
                      </ListItem>
                    );
                  })}
                </List>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Quick Actions - Only for Employee users */}
        {user && user.role === 'employee' && (
          <Grid item xs={12} md={4}>
            <Card>
              <CardHeader title="Quick Actions" />
              <CardContent>
                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 1 }}>
                  <Button 
                    variant="contained" 
                    color="primary" 
                    size="small"
                    onClick={() => window.location.href = '/my-assignments'}
                    sx={{ flex: { xs: 'none', sm: 1 }, minWidth: { xs: '100%', sm: '120px' } }}
                  >
                    My Assignments
                  </Button>
                  <Button 
                    variant="outlined" 
                    color="info" 
                    size="small"
                    onClick={() => window.location.href = '/profile'}
                    sx={{ flex: { xs: 'none', sm: 1 }, minWidth: { xs: '100%', sm: '120px' } }}
                  >
                    My Profile
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>
    </Box>
  );
};

export default Dashboard; 
